import { Controller } from "@hotwired/stimulus"

/**
 * Translations Controller
 * Handles loading and managing translations for JavaScript components
 */
export default class extends Controller {
  static values = {
    scope: String,
    locale: String
  }

  connect() {
    this.loadTranslations();
    this.setupGlobalAccess();
  }

  /**
   * Load translations from data attributes or DOM
   */
  loadTranslations() {
    try {
      // Try to get translations from data attribute
      const translationsData = this.element.dataset.translations;
      if (translationsData) {
        const data = JSON.parse(translationsData);
        this.translations = data.translations || data;
        this.locale = data.locale || this.localeValue || 'fr';
        return;
      }

      // Fallback to global translation service
      if (window.TranslationService) {
        this.translations = window.TranslationService.translations;
        this.locale = window.TranslationService.locale;
      } else {
        console.warn('No translations found for translations controller');
        this.translations = {};
        this.locale = this.localeValue || 'fr';
      }
    } catch (error) {
      console.error('Error loading translations in controller:', error);
      this.translations = {};
      this.locale = this.localeValue || 'fr';
    }
  }

  /**
   * Setup global access to translations
   */
  setupGlobalAccess() {
    // Make translations available to child elements
    this.element.translationService = this;
    
    // Dispatch event to notify other components
    this.dispatch('loaded', {
      detail: {
        translations: this.translations,
        locale: this.locale,
        scope: this.scopeValue
      }
    });
  }

  /**
   * Translate a key
   * @param {string} key - Translation key
   * @param {Object} options - Translation options
   * @returns {string} Translated text
   */
  t(key, options = {}) {
    try {
      let translation = this.getTranslation(key);
      
      if (translation === null || translation === undefined) {
        return this.handleMissingTranslation(key, options);
      }

      // Handle pluralization
      if (typeof translation === 'object' && options.count !== undefined) {
        translation = this.pluralize(translation, options.count);
      }

      // Handle interpolations
      if (typeof translation === 'string' && options) {
        translation = this.interpolate(translation, options);
      }

      return translation;
    } catch (error) {
      console.error(`Translation error for key "${key}":`, error);
      return this.handleMissingTranslation(key, options);
    }
  }

  /**
   * Get translation by key path
   * @param {string} key - Dot notation key
   * @returns {*} Translation value
   */
  getTranslation(key) {
    const keys = key.split('.');
    let current = this.translations;

    for (const k of keys) {
      if (current && typeof current === 'object' && k in current) {
        current = current[k];
      } else {
        return null;
      }
    }

    return current;
  }

  /**
   * Handle pluralization
   * @param {Object} translations - Plural translations object
   * @param {number} count - Count for pluralization
   * @returns {string} Pluralized translation
   */
  pluralize(translations, count) {
    if (typeof translations !== 'object') {
      return translations;
    }

    // Handle French pluralization rules
    if (this.locale === 'fr') {
      if (count === 0 && translations.zero) {
        return translations.zero;
      } else if (count === 1 && translations.one) {
        return translations.one;
      } else if (translations.other) {
        return translations.other;
      }
    }

    // Handle English pluralization rules
    if (this.locale === 'en') {
      if (count === 0 && translations.zero) {
        return translations.zero;
      } else if (count === 1 && translations.one) {
        return translations.one;
      } else if (translations.other) {
        return translations.other;
      }
    }

    // Fallback to 'other' or first available
    return translations.other || translations.one || Object.values(translations)[0] || '';
  }

  /**
   * Interpolate variables in translation string
   * @param {string} translation - Translation string with placeholders
   * @param {Object} options - Interpolation variables
   * @returns {string} Interpolated string
   */
  interpolate(translation, options) {
    let result = translation;

    // Replace %{variable} patterns
    Object.keys(options).forEach(key => {
      if (key !== 'count') {
        const pattern = new RegExp(`%\\{${key}\\}`, 'g');
        result = result.replace(pattern, options[key]);
      }
    });

    return result;
  }

  /**
   * Handle missing translations
   * @param {string} key - Missing translation key
   * @param {Object} options - Translation options
   * @returns {string} Fallback text
   */
  handleMissingTranslation(key, options = {}) {
    // Log missing translation in development
    if (this.isDevelopment()) {
      console.warn(`Missing translation: ${key}`);
    }

    // Try to provide a reasonable fallback
    const lastKey = key.split('.').pop();
    if (lastKey) {
      // Convert snake_case to Title Case
      const humanized = lastKey.replace(/_/g, ' ')
                              .replace(/\b\w/g, l => l.toUpperCase());
      return humanized;
    }

    return `[missing "${key}" translation]`;
  }

  /**
   * Check if we're in development environment
   * @returns {boolean}
   */
  isDevelopment() {
    return document.body.dataset.environment === 'development' ||
           window.location.hostname === 'localhost' ||
           window.location.hostname === '127.0.0.1';
  }

  /**
   * Check if a translation exists
   * @param {string} key - Translation key
   * @returns {boolean}
   */
  exists(key) {
    return this.getTranslation(key) !== null;
  }

  /**
   * Get all translations for a scope
   * @param {string} scope - Translation scope
   * @returns {Object} Scoped translations
   */
  scope(scope) {
    return this.getTranslation(scope) || {};
  }

  /**
   * Reload translations
   */
  reload() {
    this.loadTranslations();
    this.setupGlobalAccess();
  }

  /**
   * Action to handle locale changes
   */
  localeChanged(event) {
    this.locale = event.detail.locale || event.target.value;
    this.reload();
  }
}